import React, { useMemo } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { BoardCards, HoleCards } from './Cards';
import { formatAmount, formatClockTime, formatPot } from './format';
import { HandCardBody, useHasMounted } from './HandCardBody';
import { HandSnapshot } from './HandSnapshot';
import {
  getPotTotal,
  getRunouts,
  getViewerCards,
  getViewerGrossWin,
  getViewerNet,
  getViewerRunOutcomes,
  isUncontestedWin,
  withoutWinningCards
} from './handSummary';
import { CardRef, GameType, HandHistorySnapshot } from './types';
import { useOwnCardsRecovery } from './useOwnCardsRecovery';
import { withWinningCards } from './winningCards';
import { getViewerRunWins, getViewerWinSummary, ViewerRunWin } from './winSummary';

import styles from './HandHistory.module.scss';

/** Marks each card's toggle so the panel can walk them with ↑/↓. */
export const HAND_CARD_TOGGLE_ATTR = 'data-hand-card-toggle';

/**
 * A multi-run hand's boards, one row per run: "Run n", then that run's board —
 * the hole cards are shown once above them, not repeated (EN-11110 A1). The
 * labels and the boards are the cells of ONE grid whose label column is as
 * wide as the widest label, so every run's first card starts at the same x
 * (A2). Shared by the completed and the live card.
 */
export const RunRows = ({
  boards,
  renderBoard,
  resultOf,
  renderWin
}: {
  boards: CardRef[][];
  renderBoard: (board: CardRef[], index: number) => React.ReactNode;
  /** The run's result for the viewer, as `data-run-result` on its board. */
  resultOf?: (index: number) => string | undefined;
  /** What the viewer won on the run, in a third cell at the row's end. */
  renderWin?: (index: number) => React.ReactNode;
}) => (
  <span
    className={`${styles.handCardRuns} ${renderWin ? styles.handCardRunsWithWins : ''}`}
    data-run-rows=''>
    {boards.map((board, index) => (
      <React.Fragment key={index}>
        <span className={styles.runPill} data-run-label=''>
          Run {index + 1}
        </span>
        <span
          className={styles.handCardRunBoard}
          data-run-board=''
          data-run-result={resultOf?.(index)}>
          {renderBoard(board, index)}
        </span>
        {renderWin && (
          <span className={styles.handCardRunWin} data-run-win=''>
            {renderWin(index)}
          </span>
        )}
      </React.Fragment>
    ))}
  </span>
);

/** One run's win beside its board: "Main · Side 1 · Side 2" over "$3.13". */
const RunWin = ({ win, gameType }: { win?: ViewerRunWin; gameType: GameType }) =>
  win ? (
    <>
      <span className={styles.handCardRunWinPots}>{win.pots.join(' · ')}</span>
      <strong className={styles.handCardRunWinAmount}>{formatPot(win.amount, gameType)}</strong>
    </>
  ) : null;

export const handCardBodyId = (hand: Pick<HandHistorySnapshot, 'gameId' | 'gameNo'>) =>
  `hand-card-${hand.gameId}-${hand.gameNo}`;

/**
 * "You won: Main (Runs 1, 3), Side 1 (Run 2)" split where it may wrap: after
 * the label, between pots, and before a pot's runs — never inside "(Runs 1,
 * 3)" or a pot's name. Each piece is short, so the summary never widens the
 * money column past the live card's and pushes the boards onto two lines.
 * Exported for test.
 */
export const winSummaryParts = (text: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  const cut = (end: number, next: number) => {
    parts.push(text.slice(start, end));
    start = next;
  };
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (depth === 0 && char === ' ' && text[index + 1] === '(') cut(index, index + 1);
    if (char === '(') depth += 1;
    if (char === ')') depth = Math.max(0, depth - 1);
    if (depth === 0 && (char === ':' || char === ',') && text[index + 1] === ' ') {
      cut(index + 1, index + 2);
    }
  }
  parts.push(text.slice(start));
  return parts.filter(Boolean);
};

/** The win summary in the money column: wraps between its parts only. */
export const WinSummaryText = ({ text }: { text: string }) => (
  <>
    {winSummaryParts(text).map((part, index) => (
      <React.Fragment key={index}>
        {index > 0 && ' '}
        <span className={styles.handCardSummaryPart}>{part}</span>
      </React.Fragment>
    ))}
  </>
);

/** "Hand 83010" - the felt's wording without the table, which the panel header carries once. */
export const HandTitle = ({ gameNo }: { gameNo?: number | string }) => <>Hand {gameNo ?? '—'}</>;

/**
 * One completed hand in the list.
 *
 * Collapsed, the header answers the question players actually ask ("what
 * happened when I had the pair of twos?"): hand number and time, the viewer's
 * hole cards, the board (every runout of a multi-run hand), the pot and the
 * viewer's net. When the viewer won, the five cards that made the hand stay
 * bright and the rest dim. A multi-run hand shows one row per runout, the
 * hole cards repeated on each so the "Run n" tags and boards line up, and
 * each row lit by its own result as the record tags it: the run's winning five
 * bright, a lost run dimmed whole when another run was won (Jason, 28 Sep; see
 * getViewerRunOutcomes). Expanding shows the payouts, the table snapshot and
 * the play-by-play.
 */
export const HandCard = ({
  hand,
  expanded,
  onToggle
}: {
  hand: HandHistorySnapshot;
  expanded: boolean;
  /** Receives the tap so the panel can keep the tapped header where it is. */
  onToggle: (event?: React.SyntheticEvent<HTMLElement>) => void;
}) => {
  const bodyId = handCardBodyId(hand);
  // The payout rows light a winner's five too; on a win nobody had to show for
  // there is nothing to light (see isUncontestedWin).
  const snapshotHand = useMemo(
    () => (isUncontestedWin(hand) ? withoutWinningCards(hand) : hand),
    [hand]
  );
  const viewerCards = getViewerCards(hand);
  // Each run lit by the five the viewer played on it. The record keeps the
  // engine's five for the board that settled last only, so earlier runs' fives
  // are completed from their own boards first (withWinningCards), as the body
  // does — otherwise only the last run's board lit up (Hand 89).
  const outcomes = useMemo(() => getViewerRunOutcomes(withWinningCards(hand)), [hand]);
  const onScreen = useHasMounted();
  const net = getViewerNet(hand);
  const grossWin = getViewerGrossWin(hand);
  const potTotal = getPotTotal(hand);
  // What the viewer took, in the pot's place; the total for an observer or a
  // viewer who won nothing.
  const winSummary = getViewerWinSummary(hand);
  // ONE result. "Won $X" is everything the viewer won — every pot, half and
  // run, as the payout rows' "Won" amounts add up — never the net of what they
  // put in (Hand 87: $1.33 + $2.98 + $5.97 + $5.96 = $16.24, not the +$6.29
  // net). Known even when the net is not (a multi-run hand settled before its
  // contributions were recorded). A viewer who won nothing sees their loss,
  // "-$X", in the loss colour; nothing at break-even. An older record whose
  // rows carry no gross shows its profit as the signed net, "+$X": "Won" is
  // only ever what was collected, never the net (won $32 on $22 is +$10).
  const wonAmount = grossWin !== undefined && grossWin > 0 ? grossWin : undefined;
  const netGainAmount =
    grossWin === undefined && !hand.netUnavailable && net !== undefined && net > 0
      ? net
      : undefined;
  const lossAmount =
    wonAmount === undefined && !hand.netUnavailable && net !== undefined && net < 0
      ? net
      : undefined;
  const resultClass =
    wonAmount !== undefined || netGainAmount !== undefined
      ? styles.positive
      : lossAmount !== undefined
      ? styles.negative
      : '';
  const boards = getRunouts(hand);
  const isMultiRun = boards.length > 1;
  // What the viewer won on each run, beside its board, in place of the "You
  // won: …" sentence; the money column then keeps only the total, in the title
  // row, so the run rows get the header's width.
  const runWins = isMultiRun ? getViewerRunWins(hand, boards.length) : undefined;
  // A multi-run hand shows the hole cards ONCE (EN-11110 A1), lit by every
  // five they played in a run the viewer won; each run row lights its own.
  const heroHighlight = isMultiRun
    ? outcomes.flatMap(({ result, winningCards }) =>
        result === 'won' && winningCards ? winningCards : []
      )
    : outcomes[0]?.winningCards;

  // "Your cards" belong in the collapsed row, so recover them for every card,
  // not only the one that is open.
  useOwnCardsRecovery(hand);

  // No scrollIntoView on expand: the tapped header stays where it is and the
  // body opens under it (the panel's useStableToggle keeps it there).
  return (
    <article
      data-hand-card='completed'
      className={`${styles.handCard} ${expanded ? styles.handCardExpanded : ''}`}>
      <button
        type='button'
        className={`${styles.handCardHeader} ${isMultiRun ? styles.handCardHeaderMultiRun : ''}`}
        aria-expanded={expanded}
        aria-controls={bodyId}
        {...{ [HAND_CARD_TOGGLE_ATTR]: '' }}
        onClick={onToggle}>
        <span className={styles.handCardTitle}>
          <strong>
            <HandTitle gameNo={hand.gameNo} />
          </strong>
          <span className={styles.handCardTime}>{formatClockTime(hand.completedAt)}</span>
        </span>
        <span className={styles.handCardCards}>
          {isMultiRun ? (
            <>
              {viewerCards.cards.length > 0 && (
                <span className={styles.handCardHole}>
                  <HoleCards
                    cards={viewerCards.cards}
                    revealed={viewerCards.revealed}
                    size='base'
                    highlight={heroHighlight}
                  />
                </span>
              )}
              <RunRows
                boards={boards}
                renderBoard={(board, index) => {
                  const { result, winningCards } = outcomes[index] ?? {};
                  return (
                    <>
                      {/* The dimming says it visually; this says it to a screen reader. */}
                      {result && <span className={styles.srOnly}>{`, ${result}`}</span>}
                      <BoardCards
                        cards={board}
                        emptyText='Pre-flop'
                        size='base'
                        highlight={winningCards}
                        dimmed={result === 'lost'}
                      />
                    </>
                  );
                }}
                resultOf={(index) => outcomes[index]?.result}
                renderWin={
                  runWins
                    ? (index) => <RunWin win={runWins[index]} gameType={hand.gameType} />
                    : undefined
                }
              />
            </>
          ) : (
            <span className={styles.handCardRun}>
              {viewerCards.cards.length > 0 && (
                <HoleCards
                  cards={viewerCards.cards}
                  revealed={viewerCards.revealed}
                  size='base'
                  highlight={heroHighlight}
                />
              )}
              <span className={styles.handCardBoard}>
                <BoardCards
                  cards={boards[0]}
                  emptyText='Pre-flop'
                  size='base'
                  highlight={heroHighlight}
                />
              </span>
            </span>
          )}
        </span>
        <span className={styles.handCardMoney}>
          <span className={`${styles.handCardResult} ${resultClass}`}>
            {wonAmount !== undefined
              ? `Won ${formatPot(wonAmount, hand.gameType)}`
              : netGainAmount !== undefined || lossAmount !== undefined
              ? formatAmount(netGainAmount ?? (lossAmount as number), hand.gameType)
              : ''}
          </span>
          {runWins ? null : winSummary ? (
            <span className={`${styles.handCardPot} ${styles.handCardWinSummary}`}>
              <WinSummaryText text={winSummary} />
            </span>
          ) : (
            <span className={styles.handCardPot}>Pot {formatPot(potTotal, hand.gameType)}</span>
          )}
        </span>
        <span
          className={`${styles.rowChevron} ${expanded ? styles.rowChevronOpen : ''}`}
          aria-hidden='true'>
          <KeyboardArrowDownIcon />
        </span>
      </button>
      {expanded && (
        <HandCardBody id={bodyId} unfold={onScreen}>
          <HandSnapshot hand={snapshotHand} />
        </HandCardBody>
      )}
    </article>
  );
};
